import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../providers/AuthProvider';

/**
 * NEXORA AI Platform
 * AuthCallback Component
 * 
 * Handles Google OAuth callback
 * Exchanges session_id for user data and session_token
 * 
 * CRITICAL RULES:
 * - NO hardcoded URLs
 * - NO fallback URLs
 * - Use useRef to prevent race conditions
 * - Process session_id synchronously
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // CRITICAL: Prevent double-execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        console.log('[AuthCallback] Processing OAuth callback', {
          hasHash: !!hash,
          hashLength: hash?.length,
          hasSessionId: !!sessionId,
          sessionIdPrefix: sessionId ? sessionId.substring(0, 8) + '...' : 'none',
          timestamp: new Date().toISOString()
        });

        if (!sessionId) {
          console.error('[AuthCallback] No session_id found in URL hash:', hash);
          navigate('/login', {
            state: {
              error: 'No authentication session found. Please try again.'
            }
          });
          return;
        }

        // Exchange session_id for user data
        const response = await axios.post(
          `${API_URL}/api/auth/oauth/callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        // Set user in AuthContext
        setAuthUser(response.data.user);

        // Clean URL and redirect to dashboard
        window.history.replaceState({}, document.title, '/app/dashboard');
        navigate('/app/dashboard', { replace: true, state: { user: response.data.user } });

      } catch (error) {
        console.error('[AuthCallback] OAuth callback error:', {
          message: error.message,
          status: error.response?.status,
          detail: error.response?.data?.detail,
          errorId: error.response?.data?.error_id
        });
        navigate('/login', { 
          state: { 
            error: error.response?.data?.detail || 'Authentication failed. Please try again.' 
          } 
        });
      }
    };

    processCallback();
  }, [navigate, setAuthUser, API_URL]);

  // Show minimal loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-secondary">Completing authentication...</p>
      </div>
    </div>
  );
}
